import { useTranslation } from 'react-i18next';
import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Center,
  CloseButton,
  Spinner,
  Text,
  SimpleGrid,
  Table,
  VStack,
  HStack,
  Separator,
} from '@chakra-ui/react';
import {
  TableOptions,
  DataTable,
  Pagination,
  StatusBadge,
  type Column,
  type StatusType,
  type ToggleOption,
} from '@themes/aegix';
import { SelectInput, Button as ButtonComponent } from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { useUser, useUsers } from '../../hooks/queries';
import {
  usePlanStaffAssignments,
  usePlanTemplates,
  useCreatePlanStaffAssignment,
  useUpdatePlanStaffAssignment,
  useDeletePlanStaffAssignment,
} from '../../hooks/queries/useReunification';
import { toaster } from '../ui/toaster';
import type { UserFormInitialValues } from '../../types/UserFormInitialValue';
import type {
  PlanStaffAssignment,
  StaffRole,
} from '../../types/ReunificationTypes';
import { STAFF_ROLE_OPTIONS } from '../../types/ReunificationTypes';
import { useSearchParams } from 'react-router-dom';

const toggleOptions: ToggleOption[] = [
  { value: 'all', label: 'All', 'data-e2e': 'staff-filter-all' },
  { value: 'active', label: 'Active', 'data-e2e': 'staff-filter-active' },
  { value: 'inactive', label: 'Inactive', 'data-e2e': 'staff-filter-inactive' },
];

const UNASSIGNED_VALUE = '__unassigned__';

interface PendingOverride {
  tempId: string;
  templateId: string;
  role: string;
}

let nextTempId = 0;

const roleSelectOptions = [
  { value: UNASSIGNED_VALUE, label: 'Unassigned' },
  ...STAFF_ROLE_OPTIONS,
];

interface StaffRow {
  [key: string]: unknown;
  id: string;
  userId: number;
  name: string;
  role: string;
  roleAssignmentId: string | null;
  email: string;
  status: StatusType;
}

const columns: Column<StaffRow>[] = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    filterable: true,
    width: '220px',
  },
  {
    key: 'role',
    label: 'Default Role',
    sortable: false,
    filterable: true,
    width: '200px',
  },
  {
    key: 'email',
    label: 'Email',
    sortable: false,
    filterable: true,
    width: '260px',
  },
  {
    key: 'status',
    label: 'Status',
    sortable: false,
    filterable: true,
    filterOptions: [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ],
    width: '120px',
    render: (row) => <StatusBadge status={row.status} />,
  },
];

function buildRoleMap(
  assignments: PlanStaffAssignment[] | undefined,
): Map<number, PlanStaffAssignment> {
  const map = new Map<number, PlanStaffAssignment>();
  if (!assignments) return map;
  for (const a of assignments) {
    // Only include default assignments (plan_template === null)
    if (a.plan_template === null) {
      map.set(a.user, a);
    }
  }
  return map;
}

function transformStaff(
  users: UserFormInitialValues[],
  roleMap: Map<number, PlanStaffAssignment>,
  t: (key: string, fallback: string) => string,
): StaffRow[] {
  return users.map((user) => {
    const userId = Number(user.id) || 0;
    const assignment = roleMap.get(userId);
    return {
      id: String(user.id ?? ''),
      userId,
      name: `${user.first_name} ${user.last_name}`.trim() || user.email,
      role: assignment?.role_display ?? t('common.unassigned', 'Unassigned'),
      roleAssignmentId: assignment?.public_id ?? null,
      email: user.email,
      status: user.is_active ? 'active' : ('inactive' as StatusType),
    };
  });
}

export function StaffList() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('id');
  const urlTab = searchParams.get('tab');
  const isStaffTab = !urlTab || urlTab === 'staff';
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState<StaffRow>();
  const [selectedRole, setSelectedRole] = useState<string>(UNASSIGNED_VALUE);
  const [pendingAdds, setPendingAdds] = useState<PendingOverride[]>([]);
  const [pendingEdits, setPendingEdits] = useState<Record<string, string>>({});
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const roleSelectOptions = useMemo(
    () => [
      { value: UNASSIGNED_VALUE, label: t('common.unassigned', 'Unassigned') },
      ...STAFF_ROLE_OPTIONS,
    ],
    [t],
  );

  const { data, isLoading, isFetching, error } = useUsers({
    search: searchValue || undefined,
    is_active:
      statusFilter === 'active'
        ? true
        : statusFilter === 'inactive'
          ? false
          : undefined,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    ordering:
      sortColumn === 'name' && sortDirection
        ? sortDirection === 'desc'
          ? '-name'
          : 'name'
        : 'name',
  });
  const { data: userById } = useUser(Number(urlId));

  // Fetch default role assignments (plan_template is null)
  // Use max page_size to ensure roleMap covers all staff, not just first 20
  const { data: roleData } = usePlanStaffAssignments({
    plan_template__isnull: true,
    page_size: 100,
  });

  // Fetch all assignments for the selected user (default + overrides)
  const { data: userAssignmentData, isLoading: userAssignmentsLoading } =
    usePlanStaffAssignments(
      { user: currentRow?.userId },
      { enabled: drawerOpen && !!currentRow },
    );

  // Fetch templates for the override dropdown
  const { data: templateData } = usePlanTemplates({});

  const createMutation = useCreatePlanStaffAssignment();
  const updateMutation = useUpdatePlanStaffAssignment();
  const deleteMutation = useDeletePlanStaffAssignment();

  const roleMap = useMemo(
    () => buildRoleMap(roleData?.results),
    [roleData?.results],
  );

  // Find the user's default assignment from per-user data (avoids pagination issues with global roleData)
  const userDefault = useMemo(() => {
    if (!userAssignmentData?.results) return undefined;
    return userAssignmentData.results.find((a) => a.plan_template === null);
  }, [userAssignmentData?.results]);

  // Separate per-user assignments into overrides (non-null plan_template)
  const userOverrides = useMemo(() => {
    if (!userAssignmentData?.results) return [];
    return userAssignmentData.results.filter((a) => a.plan_template !== null);
  }, [userAssignmentData?.results]);

  // Visible overrides: existing minus pending deletes
  const visibleOverrides = useMemo(() => {
    return userOverrides.filter((o) => !pendingDeletes.has(o.public_id));
  }, [userOverrides, pendingDeletes]);

  // Available templates for new overrides (exclude existing visible overrides)
  const availableTemplates = useMemo(() => {
    if (!templateData?.results) return [];
    const assignedIds = new Set(visibleOverrides.map((o) => o.plan_template));
    return templateData.results
      .filter((t) => !assignedIds.has(t.public_id))
      .map((t) => ({ value: t.public_id, label: t.name }));
  }, [templateData?.results, visibleOverrides]);

  // Whether more templates can be added (accounting for pending rows)
  const canAddMore = useMemo(() => {
    return availableTemplates.length > pendingAdds.length;
  }, [availableTemplates, pendingAdds]);
  const isSingleStaffView = Boolean(urlId && isStaffTab && userById);
  const staffList = isSingleStaffView
    ? [userById as unknown as UserFormInitialValues]
    : data?.results;
  const staff = staffList ? transformStaff(staffList, roleMap, t) : [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
  }, []);

  const handleFilterClear = useCallback((column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  const filteredStaff = useMemo(() => {
    const activeFilters = Object.entries(filterValues).filter(([, v]) => v);
    if (activeFilters.length === 0) return staff;
    return staff.filter((row) =>
      activeFilters.every(([col, val]) => {
        const cellValue = String(
          (row as Record<string, unknown>)[col] ?? '',
        ).toLowerCase();
        return cellValue.includes(val.toLowerCase());
      }),
    );
  }, [staff, filterValues]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, statusFilter, pageSize, filterValues]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const openEditDrawer = useCallback(
    (row: StaffRow) => {
      setCurrentRow(row);
      // Look up current role from roleMap
      const assignment = roleMap.get(row.userId);
      setSelectedRole(assignment?.role ?? UNASSIGNED_VALUE);
      setDrawerOpen(true);
    },
    [roleMap],
  );

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setCurrentRow(undefined);
    setSelectedRole(UNASSIGNED_VALUE);
    setPendingAdds([]);
    setPendingEdits({});
    setPendingDeletes(new Set());
  };

  const handleSave = async () => {
    if (!currentRow) return;

    try {
      // --- Default role ---
      const assignment = userDefault;
      if (selectedRole === UNASSIGNED_VALUE) {
        if (assignment) {
          await deleteMutation.mutateAsync(assignment.public_id);
        }
      } else if (assignment) {
        if (assignment.role !== selectedRole) {
          await updateMutation.mutateAsync({
            id: assignment.public_id,
            data: { role: selectedRole as StaffRole },
          });
        }
      } else {
        await createMutation.mutateAsync({
          user: currentRow.userId,
          role: selectedRole as StaffRole,
          plan_template: null,
        });
      }

      // --- Override deletes ---
      for (const id of pendingDeletes) {
        await deleteMutation.mutateAsync(id);
      }

      // --- Override edits ---
      for (const [id, newRole] of Object.entries(pendingEdits)) {
        if (pendingDeletes.has(id)) continue;
        await updateMutation.mutateAsync({
          id,
          data: { role: newRole as StaffRole },
        });
      }

      // --- Override creates ---
      for (const pending of pendingAdds) {
        if (!pending.templateId || !pending.role) continue;
        await createMutation.mutateAsync({
          user: currentRow.userId,
          role: pending.role as StaffRole,
          plan_template: pending.templateId,
        });
      }

      toaster.create({ title: t('common.changesSaved', 'Changes saved'), type: 'success' });
      handleDrawerClose();
    } catch (err) {
      toaster.create({
        title: t('reunification.failedSaveStaffRoles', 'Failed to save changes'),
        description: err instanceof Error ? err.message : t('common.unknownError', 'Unknown error'),
        type: 'error',
      });
    }
  };

  const handleOverrideRoleChange = useCallback(
    (assignmentId: string, newRole: string) => {
      setPendingEdits((prev) => ({ ...prev, [assignmentId]: newRole }));
    },
    [],
  );

  const handleDeleteOverride = useCallback((assignmentId: string) => {
    setPendingDeletes((prev) => new Set(prev).add(assignmentId));
  }, []);

  const handleDeletePendingAdd = useCallback((tempId: string) => {
    setPendingAdds((prev) => prev.filter((p) => p.tempId !== tempId));
  }, []);

  const handleAddOverrideRow = useCallback(() => {
    setPendingAdds((prev) => [
      ...prev,
      { tempId: `temp-${++nextTempId}`, templateId: '', role: '' },
    ]);
  }, []);

  const handlePendingTemplateChange = useCallback(
    (tempId: string, templateId: string) => {
      setPendingAdds((prev) =>
        prev.map((p) => (p.tempId === tempId ? { ...p, templateId } : p)),
      );
    },
    [],
  );

  const handlePendingRoleChange = useCallback(
    (tempId: string, role: string) => {
      setPendingAdds((prev) =>
        prev.map((p) => (p.tempId === tempId ? { ...p, role } : p)),
      );
    },
    [],
  );

  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  const hasIncompletePending = pendingAdds.some(
    (p) => !p.templateId || !p.role,
  );
  const toggleOptions: ToggleOption[] = useMemo(() => [
    { value: 'all', label: t('common.all', 'All') },
    { value: 'active', label: t('common.active', 'Active') },
    { value: 'inactive', label: t('common.inactive', 'Inactive') },
  ], [t]);

  const columns: Column<StaffRow>[] = useMemo(() => [
    {
      key: 'name',
      label: t('common.name', 'Name'),
      sortable: true,
      filterable: true,
      width: '220px',
    },
    {
      key: 'role',
      label: t('reunification.defaultRole', 'Default Role'),
      sortable: true,
      filterable: true,
      width: '220px',
    },
    {
      key: 'email',
      label: t('common.email', 'Email'),
      sortable: false,
      filterable: true,
      width: '260px',
    },
    {
      key: 'status',
      label: t('common.status', 'Status'),
      sortable: false,
      filterable: true,
      filterOptions: [
        { label: t('common.active', 'Active'), value: 'active' },
        { label: t('common.inactive', 'Inactive'), value: 'inactive' },
      ],
      width: '120px',
      render: (row) => (
        <StatusBadge
          status={row.status}
          label={t(`common.${row.status}`, row.status)}
        />
      ),
    },
  ], [t]);

  const saveDisabled =
    isSaving || (drawerOpen && userAssignmentsLoading) || hasIncompletePending;

  return (
    <>
      <TableOptions
        toggleOptions={toggleOptions}
        activeToggle={statusFilter}
        onToggleChange={(v: string) => {
          setStatusFilter(v);
          setCurrentPage(1);
        }}
        searchValue={searchValue}
        onSearchChange={(v) => {
          setSearchValue(v);
          setCurrentPage(1);
        }}
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error ? error.message : t('reunification.failedLoadStaff', 'Failed to load staff')}
            </Text>
          </Center>
        ) : staff.length === 0 ? (
          <Center py={12}>
            {isFetching ? (
              <Spinner size="lg" color="button.primary" />
            ) : (
              <Text color="text.secondary">{t('reunification.noStaffFound', 'No staff found')}</Text>
            )}
          </Center>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={filteredStaff}
              keyExtractor={(row) => row.id}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onFilterClear={handleFilterClear}
              onRowClick={openEditDrawer}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}

        <DrawerComponent
          isOpen={drawerOpen}
          onClose={handleDrawerClose}
          title={t('reunification.editStaffRole', 'Edit Staff Role')}
          size="md"
          closeOnOutside={false}
          footer={
            <SimpleGrid columns={2} gap={4} width="100%">
              <ButtonComponent
                visual="secondary"
                type="button"
                onClick={handleDrawerClose}
                data-e2e="cancel-staff-role-button"
              >
                {t('common.cancel', 'Cancel')}
              </ButtonComponent>
              <ButtonComponent
                visual="primary"
                type="button"
                onClick={handleSave}
                disabled={saveDisabled}
                data-e2e="save-staff-role-button"
              >
                {isSaving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
              </ButtonComponent>
            </SimpleGrid>
          }
        >
          {currentRow && (
            <VStack gap={4} align="stretch">
              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="text.secondary"
                  mb={1}
                >
                  {t('reunification.staffMember', 'Staff Member')}
                </Text>
                <Text fontSize="md" color="text.primary">
                  {currentRow.name}
                </Text>
              </Box>

              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="text.secondary"
                  mb={1}
                >
                  {t('common.email', 'Email')}
                </Text>
                <Text fontSize="md" color="text.primary">
                  {currentRow.email}
                </Text>
              </Box>

              <SelectInput
                label={t('reunification.defaultRole', 'Default Role')}
                name="default_role"
                value={selectedRole}
                onChange={(val: string) => setSelectedRole(val)}
                option={roleSelectOptions}
                placeholder={t('reunification.selectRole', 'Select a role')}
                e2eTitle="staff-default-role-select"
              />

              <Separator />

              <Box>
                <HStack justify="space-between" mb={3}>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="text.secondary"
                  >
                    {t('reunification.templateRoleOverrides', 'Template Role Overrides')}
                  </Text>
                  {!userAssignmentsLoading && canAddMore && (
                    <ButtonComponent
                      visual="secondary"
                      size="sm"
                      type="button"
                      onClick={handleAddOverrideRow}
                      data-e2e="add-override-button"
                    >
                      {t('common.add', 'Add')}
                    </ButtonComponent>
                  )}
                </HStack>

                {userAssignmentsLoading ? (
                  <Center py={4}>
                    <Spinner size="sm" color="button.primary" />
                  </Center>
                ) : visibleOverrides.length === 0 &&
                  pendingAdds.length === 0 ? (
                  <Text fontSize="sm" color="text.tertiary">
                    {t('reunification.noTemplateRoleOverrides', 'No template-specific overrides')}
                  </Text>
                ) : (
                  <Table.Root size="sm" variant="outline">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader
                          color="text.secondary"
                          fontSize="12px"
                          fontWeight={600}
                        >
                          {t('reunification.template', 'Template')}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader
                          color="text.secondary"
                          fontSize="12px"
                          fontWeight={600}
                        >
                          {t('common.role', 'Role')}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader width="32px" />
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {visibleOverrides.map((override) => (
                        <Table.Row key={override.public_id}>
                          <Table.Cell
                            fontSize="sm"
                            color="text.primary"
                            verticalAlign="middle"
                          >
                            {override.plan_template_name ?? t('reunification.unknownTemplate', 'Unknown Template')}
                          </Table.Cell>
                          <Table.Cell>
                            <SelectInput
                              label=""
                              name={`override_role_${override.public_id}`}
                              value={
                                pendingEdits[override.public_id] ??
                                override.role
                              }
                              onChange={(val: string) =>
                                handleOverrideRoleChange(
                                  override.public_id,
                                  val,
                                )
                              }
                              option={STAFF_ROLE_OPTIONS}
                              e2eTitle={`staff-override-role-${override.public_id}`}
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <CloseButton
                              size="sm"
                              onClick={() =>
                                handleDeleteOverride(override.public_id)
                              }
                            />
                          </Table.Cell>
                        </Table.Row>
                      ))}
                      {pendingAdds.map((pending) => {
                        // Exclude templates selected by other pending rows
                        const otherSelected = new Set(
                          pendingAdds
                            .filter(
                              (p) =>
                                p.tempId !== pending.tempId && p.templateId,
                            )
                            .map((p) => p.templateId),
                        );
                        const rowTemplates = availableTemplates.filter(
                          (t) => !otherSelected.has(t.value),
                        );
                        return (
                          <Table.Row key={pending.tempId}>
                            <Table.Cell>
                              <SelectInput
                                label=""
                                name={`pending_template_${pending.tempId}`}
                                value={pending.templateId}
                                onChange={(val: string) =>
                                  handlePendingTemplateChange(
                                    pending.tempId,
                                    val,
                                  )
                                }
                                option={rowTemplates}
                                placeholder={t('reunification.selectTemplate', 'Select template')}
                                e2eTitle={`staff-pending-template-${pending.tempId}`}
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <SelectInput
                                label=""
                                name={`pending_role_${pending.tempId}`}
                                value={pending.role}
                                onChange={(val: string) =>
                                  handlePendingRoleChange(pending.tempId, val)
                                }
                                option={STAFF_ROLE_OPTIONS}
                                placeholder={t('reunification.selectRole', 'Select a role')}
                                e2eTitle={`staff-pending-role-${pending.tempId}`}
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <CloseButton
                                size="sm"
                                onClick={() =>
                                  handleDeletePendingAdd(pending.tempId)
                                }
                              />
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table.Root>
                )}
              </Box>
            </VStack>
          )}
        </DrawerComponent>
      </Box>
    </>
  );
}
